import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ClipboardList, Shield, Clock, CheckCircle } from 'lucide-react';
import '../styles/GuestIntake.css';

const GuestIntake = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('cadence_token');
    if (token) {
      navigate('/portal?tab=intake');
    }
  }, [navigate]);

  const handleQuickStart = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Store email for later account creation
    sessionStorage.setItem('guest_intake_email', email);
    
    // Redirect to login with intake redirect
    navigate(`/login?redirect=intake&email=${encodeURIComponent(email)}`);
  };

  return (
    <div className="guest-intake-page">
      <div className="guest-intake-container">
        {/* Header */}
        <div className="guest-intake-header">
          <img 
            src="/cadence-emblem-velvet.png" 
            alt="CADENCE" 
            className="guest-intake-logo"
          />
          <h1 className="guest-intake-title">Health Intake Form</h1>
          <p className="guest-intake-subtitle">
            Start your journey to clarity and advocacy
          </p>
        </div>

        {/* What to expect */}
        <div className="guest-intake-info">
          <h2 className="info-title">What to Expect</h2>
          
          <div className="info-cards">
            <div className="info-card">
              <Clock className="info-icon" />
              <h3>15-20 Minutes</h3>
              <p>Take your time. Progress is saved automatically.</p>
            </div>
            
            <div className="info-card">
              <ClipboardList className="info-icon" />
              <h3>8 Sections</h3>
              <p>Health history, symptoms, lifestyle, and goals.</p>
            </div>
            
            <div className="info-card">
              <Shield className="info-icon" />
              <h3>HIPAA Protected</h3>
              <p>Your information is confidential and secure.</p>
            </div>
          </div>
        </div>

        {/* Quick Start Form */}
        <div className="guest-intake-form-section">
          <h2 className="form-title">Get Started</h2>
          <p className="form-description">
            Enter your email to begin. You'll create a full account before submitting.
          </p>
          
          <form onSubmit={handleQuickStart} className="quick-start-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="email-input"
              />
            </div>
            
            <button type="submit" className="start-intake-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Loading...' : 'Start Health Intake'}
              <ArrowRight size={20} />
            </button>
          </form>
          
          <div className="guest-option-divider">
            <span>or</span>
          </div>
          
          <button 
            onClick={() => navigate('/portal?tab=intake&guest=true')} 
            className="continue-guest-btn"
          >
            Continue as Guest
            <ArrowRight size={18} />
          </button>
          <p className="guest-note">
            Start the survey without creating an account. You can save your progress later.
          </p>
          
          <p className="form-note">
            Already have an account? <Link to="/login?redirect=intake">Sign in here</Link>
          </p>
        </div>

        {/* Benefits */}
        <div className="guest-intake-benefits">
          <h3>Why Complete the Intake?</h3>
          <ul className="benefits-list">
            <li>
              <CheckCircle size={18} />
              <span>We review your complete health picture before your first consultation</span>
            </li>
            <li>
              <CheckCircle size={18} />
              <span>Identify patterns across symptoms, medications, and history</span>
            </li>
            <li>
              <CheckCircle size={18} />
              <span>Prepare targeted questions for your next doctor visit</span>
            </li>
            <li>
              <CheckCircle size={18} />
              <span>Get the clarity you deserve</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GuestIntake;
